// ─────────────────────────────────────────────────────────────────────────────
// WP-16 REQ-139 — Registro standalone Frontline (/app/registro).
//
// Formulario CONDICIONAL por OU: Europa pide fecha de ingreso (startDate); el resto pide número de
// nómina (employeeId). Catálogos de OU desde la API (NO hardcodeados, useRegistrationCatalogs).
// Verificación por correo (frontline-register → verify-email). Mensajes de validación.
// Al terminar el alta se ofrece "Iniciar sesión" (con AutoVerifyEmail en QA el login funciona de una;
// el selector de avatar se muestra ya autenticado, NO en esta página anónima — antes fallaba con 401
// porque no había sesión ni el id real del usuario). Consume UserAdminService.
// Criterio: "Alta frontline E2E → login".
// ─────────────────────────────────────────────────────────────────────────────
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useRegistrationCatalogs,
  useFrontlineRegister,
  useResendVerification,
  useVerifyEmail,
  useLegalDocument,
  useFrontlineAvatars,
  getRecognitionErrorMessage,
  getRecognitionErrorReason,
} from '@/api/recognition';
import type {
  FrontlineRegisterBody, FrontlineLegalAcceptance, LegalDocType, LegalDocumentDto,
} from '@/api/recognition';
import { AvatarPickerModal, UserAvatar } from './AvatarPicker';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { UserProvider } from '@/contexts/UserContext';
import { loginRequest, msalInstance } from '@/auth/msalConfig';
import {
  LanguageSelector, AuthLogo, AuthField, AuthHelp, RegisterPolicyModal, AuthErrorModal, useCooldown,
  EntraBlock,
} from './accessShared';
import '../recognition.css';
import { withBase } from '@/utils/basePath';

/**
 * Aceptaciones a registrar, o `null` si falta la versión de algún documento exigido.
 *
 * Sin versión resuelta NO se puede registrar la aceptación: grabar una inventada («1.0» a pelo, que
 * es lo que hacía el `?? '1.0'` de antes) sería un registro legal falso, sin ningún documento
 * detrás. Y al devolver el array ya construido, no queda ninguna forma de armar el cuerpo del alta
 * con una versión ausente.
 *
 * Regla #1 — las Bases Legales solo aplican a Europa; el resto de OUs únicamente acepta el Aviso de
 * Privacidad, así que ahí la versión de las Bases no se exige (ni se manda).
 */
function firmar(
  documentKey: LegalDocType,
  doc: LegalDocumentDto | undefined,
  acceptedAt: string,
  source: string,
): FrontlineLegalAcceptance | null {
  // La COMPARABLE es la del TEXTO publicado, que es la que las compuertas comparan. Si no hubiera
  // texto, lo mostrado es lo único cierto que se puede firmar (y entonces la compuerta tampoco pide
  // ese documento).
  const version = doc?.comparableVersion ?? doc?.version;
  if (!version) return null;
  return {
    // 🔴 La clave CANÓNICA. Hasta el 24-ago-2026 aquí iba `privacy-policy`/`legal-bases` para que la
    // aceptación del modal bloqueante no borrara la evidencia del PDF (la historia entera está en
    // `LEGAL_LEGACY_SIGNUP_KEYS`). Ya no hace falta: la evidencia vive en `displayedVersion`, y usar
    // la canónica es lo que hace que esta firma CUENTE y no se le vuelva a pedir a quien acaba de
    // registrarse.
    documentKey,
    version,
    acceptedAt,
    source,
    displayedVersion: doc?.contentSource === 'pdf' ? doc.version : null,
  };
}

function construirAceptaciones(
  privacyDoc: LegalDocumentDto | undefined,
  legalDoc: LegalDocumentDto | undefined,
  isEuropa: boolean,
  acceptedAt: string,
): FrontlineLegalAcceptance[] | null {
  const privacy = firmar('privacy', privacyDoc, acceptedAt, 'frontline-register');
  if (!privacy) return null;
  if (!isEuropa) return [privacy];
  const legal = firmar('legal', legalDoc, acceptedAt, 'frontline-register');
  if (!legal) return null;
  return [privacy, legal];
}

/**
 * RF-008 — ¿falta elegir avatar para poder enviar el alta?
 *
 * 🔴 Vive FUERA de `validate()` a propósito. `validate()` está al límite de
 * `sonarjs/cognitive-complexity` (tope 15) y añadirle una rama más lo rompe — pasó exactamente eso al
 * enlazar los documentos legales. La comprobación se extrae con nombre, igual que
 * `construirAceptaciones`.
 *
 * Elegir es **obligatorio** cuando hay algo que elegir. Si el catálogo viene vacío o su llamada falla,
 * **no** se bloquea el alta: convertir una caída del blob storage en «nadie puede registrarse» es un
 * daño mucho mayor que un avatar sin elegir, y la app ya cae al placeholder de iniciales. Es la única
 * excepción a la obligatoriedad, y existe por disponibilidad, no por comodidad.
 */
function faltaElegirAvatar(
  avatarId: string | null,
  catalogo: { hayOpciones: boolean; noDisponible: boolean; cargando: boolean },
): boolean {
  if (avatarId) return false;
  // Sólo se renuncia a la obligatoriedad cuando ya SE SABE que no hay nada que ofrecer: la llamada
  // falló, o terminó y vino vacía. Mientras carga todavía no se sabe, y dejar pasar el alta ahí sería
  // regalar el avatar a quien envíe rápido.
  if (catalogo.noDisponible) return false;
  if (!catalogo.cargando && !catalogo.hayOpciones) return false;
  return true;
}

function RegisterForm() {
  const { t, i18n } = useTranslation('recognition');
  const lang = i18n.language?.split('-')[0] ?? 'es';
  const register = useFrontlineRegister();
  const catalogs = useRegistrationCatalogs();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [ouId, setOuId] = useState('');
  const [numNomina, setNumNomina] = useState('');
  const [localId, setLocalId] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [acceptLegal, setAcceptLegal] = useState(false);
  const [policy, setPolicy] = useState<LegalDocType | null>(null);
  const [error, setError] = useState<string | null>(null);
  // RF-008 — el avatar elegido. No se persiste a medias: viaja en el cuerpo del alta, así que quien
  // abandone el registro y vuelva elige otra vez. Es consecuencia directa de que la pantalla sea
  // anónima (no hay cuenta todavía donde guardarlo) y está asumido, no es un olvido.
  const [avatarId, setAvatarId] = useState<string | null>(null);
  // Reabrir el modal para CAMBIAR el avatar ya elegido (clic en la carita).
  const [abrirParaCambiar, setAbrirParaCambiar] = useState(false);
  // Sólo se pone a true por la salida de D9 (catálogo vacío o con error). Es lo que impide que el
  // modal, que no se puede cerrar, deje a nadie encerrado cuando no hay nada que elegir.
  const [renuncioAlAvatar, setRenuncioAlAvatar] = useState(false);
  // Se avisa del formato al SALIR del campo, no sólo al enviar: enterarte de que el correo está mal
  // después de rellenar nómina, contraseña y aceptar el aviso legal llega tarde, y el modal del
  // envío encima tapa el campo que hay que corregir. Hasta que no se sale una vez, no se dice nada
  // — marcar en rojo lo que aún se está escribiendo es ruido.
  const [correoTocado, setCorreoTocado] = useState(false);

  // USA se RETIRA del combo de OU del registro por decisión de negocio (revierte la D-U8, que sí lo
  // listaba y permitía registrarse). Se filtra en el cliente la lista pública de OUs para no ofrecer
  // EE. UU. como organización de alta. Se compara por `name` porque el catálogo público (RegistrationOu)
  // sólo trae id/name/isEurope: el id es un GUID por ambiente (frágil) y el nombre es el código canónico
  // de la OU ("USA"), que es justo lo que ve el usuario en el desplegable.
  // Nota: esto sólo oculta la opción; el backend aún aceptaría la OU si llegara su ouId. Si se quiere
  // bloquear también el alta server-side, es un cambio aparte en el catálogo/registro de USA.
  const ous = (catalogs.data ?? []).filter((o) => o.name.trim().toUpperCase() !== 'USA');

  const selectedOu = ous.find((o) => o.id === ouId);
  const isEuropa = selectedOu?.isEurope ?? false;
  // Espejo de `EmailFormatPolicy` del servidor (que es quien manda: este endpoint es anónimo y el
  // navegador se salta con un `curl`). Aquí sólo sirve para fallar rápido, sin viaje de ida y vuelta.
  //
  // El `{2,}` del final NO es cosmético: la versión anterior terminaba en `[^\s@]+` y dejaba pasar
  // `a@a.a`, que llegaba al servidor y se guardaba tal cual — cuenta reclamada, con contraseña, y un
  // enlace de verificación que no iba a llegar a ninguna parte. Reportado en QA el 2026-08-18.
  const emailValid = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/.test(email.trim());

  // Versión legal vigente servida por el backend (trazabilidad ARCO). TODO(ARCO): cuando el endpoint
  // de documentos legales esté siempre desplegado, quitar el fallback '1.0'.
  // 🔴 MISMOS parámetros que el modal (`lang` Y `ouId`) y SIN `?? '1.0'`. Antes esta pareja de
  // llamadas resolvía la versión por su cuenta, sin OU, y con un `1.0` inventado al final: se podía
  // grabar una versión que no correspondía a lo que la persona vio, o una que no tenía ningún
  // documento detrás. Comparten `queryKey` con el modal, así que es la MISMA respuesta —la versión
  // y el documento mostrado no pueden divergir— y sin versión resuelta no se deja registrar.
  const privacyDoc = useLegalDocument('privacy', lang, ouId).data;
  const legalDoc = useLegalDocument('legal', lang, ouId).data;

  // RF-008 — la galería sale SÓLO cuando los legales exigidos ya están aceptados: Aviso de Privacidad
  // siempre, y además las Bases Legales si la OU es Europa.
  const avatars = useFrontlineAvatars();
  const legalesAceptados = acceptPrivacy && (!isEuropa || acceptLegal);
  const catalogoDeAvatares = {
    hayOpciones: (avatars.data?.length ?? 0) > 0,
    noDisponible: avatars.isError || renuncioAlAvatar,
    cargando: avatars.isLoading,
  };

  /**
   * El modal se abre SOLO —sin `useEffect`— en cuanto los legales están aceptados y no hay avatar.
   * Es una expresión y no un efecto a propósito: un efecto que abre un modal se dispara otra vez con
   * cualquier re-render que toque sus dependencias, y hay que perseguirlo con banderas.
   *
   * La única salida es elegir (que pone `avatarId`) o la puerta de D9 (`renuncioAlAvatar`), así que no
   * hace falta ningún `onClose`.
   */
  const mostrarModalDeAvatar = abrirParaCambiar
    || (legalesAceptados && !avatarId && !renuncioAlAvatar);

  const validate = (): string | null => {
    if (!fullName.trim()) return t('register.errName', 'Introduce tu nombre completo.');
    if (!emailValid) return t('register.errEmail', 'Introduce un correo electrónico válido.');
    if (!ouId) return t('register.errOu', 'Selecciona tu organización.');
    if (isEuropa) {
      if (!/^\d{1,8}$/.test(localId.trim())) return t('register.errLocalId', 'Introduce tu Local ID (hasta 8 dígitos).');
    } else if (!numNomina.trim()) {
      return t('register.errEmployeeId', 'Introduce tu número de nómina.');
    }
    if (password.length < 8) return t('register.errPassword', 'La contraseña debe tener al menos 8 caracteres.');
    if (password !== confirm) return t('register.errPasswordMatch', 'Las contraseñas no coinciden.');
    if (!acceptPrivacy) return t('register.errPrivacy', 'Debes aceptar el Aviso de Privacidad.');
    // Regla #1 — las Bases Legales (Legal Terms) SOLO aplican a Europa; el resto de OUs únicamente
    // requiere el Aviso de Privacidad.
    if (isEuropa && !acceptLegal) return t('register.errLegal', 'Debes aceptar las Bases Legales.');
    return null;
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const v = validate();
    if (v) {
      // El aviso del correo vive EN LÍNEA bajo el campo; pintarlo también en el modal deja el mismo
      // texto dos veces en pantalla. Se compara con el mensaje concreto —y no con `!emailValid`—
      // para NO alterar el orden de `validate()`: si además falta el nombre, sigue avisando del
      // nombre primero, que es el orden del formulario.
      if (v === t('register.errEmail', 'Introduce un correo electrónico válido.')) {
        setCorreoTocado(true);
        setError(null);
        return;
      }
      setError(v);
      return;
    }
    // RF-008 — la obligatoriedad del avatar se comprueba DESPUÉS de las validaciones del formulario,
    // porque la galería está al final: avisar del avatar cuando además falta la contraseña sería
    // saltarse el orden de la pantalla.
    if (faltaElegirAvatar(avatarId, catalogoDeAvatares)) {
      setError(t('avatar.required', 'Elige tu avatar para continuar.'));
      return;
    }
    const now = new Date().toISOString();
    const aceptaciones = construirAceptaciones(privacyDoc, legalDoc, isEuropa, now);
    if (!aceptaciones) {
      setError(t('register.errLegalVersion', 'No se pudo cargar el documento legal. Vuelve a intentarlo.'));
      return;
    }
    setError(null);
    const body: FrontlineRegisterBody = {
      nombre: fullName.trim(),
      correo: email.trim(),
      ouId,
      // Regla #1 — no-Europa envía número de nómina; Europa no lo tiene.
      numNomina: isEuropa ? null : numNomina.trim(),
      // QA — el selector de departamento se quitó (catálogo vacío/roto para Europa, ver historial
      // de este archivo); nunca hubo dato real que mandar aquí.
      depto: null,
      // La fecha de ingreso ya NO se pide (la reemplaza el Local ID para Europa).
      fechaIngreso: null,
      // Regla #1 — verificación de Europa: Local ID del padrón (hasta 8 dígitos, se compara completo).
      localId: isEuropa ? localId.trim() : null,
      password,
      aceptaciones,
      preferredLanguage: lang,
      // RF-008 — el id del catálogo (nombre del blob), nunca la URL. `null` si el catálogo no dio nada
      // que elegir: el backend acepta el alta sin avatar y la app cae al placeholder de iniciales.
      avatar: avatarId,
    };
    register.mutate(body);
  };

  /**
   * RF-008 — el 400 de «ese avatar ya no está», que el backend distingue del 422 del padrón con
   * `errors.reason`. Se decide por el CÓDIGO y no por el texto del título: el título viene sólo en
   * español y esta pantalla habla siete idiomas.
   *
   * Al cerrar el aviso se limpia la elección y se recarga el catálogo, y **el formulario no se toca**:
   * nombre, correo, OU, nómina, contraseña y las aceptaciones se quedan donde están. Perderlos porque
   * negocio renombró un blob mientras la pestaña estaba abierta sería un castigo absurdo — y es un caso
   * real, no teórico: el catálogo se cachea una hora.
   */
  const avatarQuedoInvalido = register.isError
    && getRecognitionErrorReason(register.error) === 'avatar-no-disponible';

  const avisoDelEnvio = (() => {
    if (error) return error;
    if (!register.isError) return null;
    if (avatarQuedoInvalido) return t('avatar.unavailable', 'Ese avatar ya no está disponible. Elige otro.');
    return getRecognitionErrorMessage(register.error, t('register.submitError', 'No se pudo completar el registro.'));
  })();

  // 🔴 Antes esta pantalla decía «Ya puedes iniciar sesión». Desde que el registro deja el correo
  // PENDIENTE de verificar, eso sería MENTIRA: el login rechaza a quien no ha abierto el enlace, y
  // con el mismo 401 que a una credencial mala — o sea, quien lo intentara creería que se equivocó
  // de contraseña. Lo que toca decir aquí es dónde está el siguiente paso.
  if (register.isSuccess) {
    return <RevisaTuCorreo correo={email.trim()} />;
  }

  return (
    <form className="acc-form" onSubmit={submit} noValidate>
      <AuthField icon="fa-user" value={fullName} onChange={setFullName} placeholder={t('register.fullName', 'Nombre completo')} autoComplete="name" />
      <AuthField
        icon="fa-envelope" type="email" value={email} onChange={setEmail}
        placeholder={t('register.email', 'Correo')} autoComplete="email"
        onBlur={() => setCorreoTocado(true)}
      />
      {/* Aviso en línea, NO en modal: el modal es para los errores del ENVÍO (convención de §7.1) y
          aquí taparía justo el campo que hay que corregir. `rp-error` es la clase que este mismo
          formulario ya usa para los avisos bajo un campo. Desaparece solo en cuanto el correo es
          válido, porque se deriva de `emailValid`. */}
      {correoTocado && email.trim() !== '' && !emailValid && (
        <p className="rp-error" role="alert">
          <i className="fas fa-triangle-exclamation" aria-hidden="true" />{' '}
          {t('register.errEmail', 'Introduce un correo electrónico válido.')}
        </p>
      )}

      <div className="acc-field">
        <select className="field" value={ouId} onChange={(e) => setOuId(e.target.value)} disabled={catalogs.isLoading} aria-label={t('register.ou', 'Organización')}>
          <option value="">{catalogs.isLoading ? t('common.loading', 'Cargando…') : t('register.selectOu', 'Selecciona tu organización')}</option>
          {ous.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        {catalogs.isError && <p className="rp-error">{t('register.catalogsError', 'No se pudo cargar el catálogo de organizaciones.')}</p>}
      </div>

      {/* Campos condicionales por región (regla #1): Europa → Local ID (hasta 8 dígitos);
          México/LATAM/FOSE/Central → número de nómina. El selector de departamento que iba aquí
          se quitó (QA): el catálogo de Europa está vacío/roto y nunca mostraba opciones. */}
      {ouId && (isEuropa ? (
        <div className="acc-field">
          <label className="acc-field-lbl" htmlFor="acc-local-id">{t('register.localId', 'Local ID')}</label>
          <input
            id="acc-local-id"
            value={localId}
            onChange={(e) => setLocalId(e.target.value.replace(/\D/g, '').slice(0, 8))}
            inputMode="numeric"
            maxLength={8}
            placeholder="00000000"
            aria-label={t('register.localId', 'Local ID')}
          />
        </div>
      ) : (
        <div className="acc-field">
          <input value={numNomina} onChange={(e) => setNumNomina(e.target.value)} placeholder={t('register.employeeId', 'Número de nómina')} aria-label={t('register.employeeId', 'Número de nómina')} />
        </div>
      ))}

      {/* Contraseña + confirmación con icono y ojo (login standalone) */}
      <AuthField icon="fa-lock" withEye value={password} onChange={setPassword} placeholder={t('register.password', 'Contraseña (mín. 8 caracteres)')} autoComplete="new-password" />
      <AuthField icon="fa-lock" withEye value={confirm} onChange={setConfirm} placeholder={t('register.confirmPassword', 'Confirmar contraseña')} autoComplete="new-password" />

      {/* Aviso de Privacidad: obligatorio para TODAS las OUs. Bases Legales (Legal Terms): solo
          Europa (regla #1) — el resto de OUs no las ve ni las necesita aceptar. */}
      <div className="acc-checkbox">
        <input
          id="acc-priv"
          type="checkbox"
          checked={acceptPrivacy}
          onChange={(e) => { if (e.target.checked) setPolicy('privacy'); else setAcceptPrivacy(false); }}
        />
        <label htmlFor="acc-priv">
          {t('register.acceptPrivacy', 'He leído y acepto el')}{' '}
          <button type="button" className="acc-policy-link" onClick={() => setPolicy('privacy')}>{t('register.privacyLink', 'Aviso de Privacidad')}</button>
        </label>
      </div>
      {isEuropa && (
      <div className="acc-checkbox">
        <input
          id="acc-legal"
          type="checkbox"
          checked={acceptLegal}
          onChange={(e) => { if (e.target.checked) setPolicy('legal'); else setAcceptLegal(false); }}
        />
        <label htmlFor="acc-legal">
          {t('register.acceptLegalBases', 'He leído y acepto las')}{' '}
          <button type="button" className="acc-policy-link" onClick={() => setPolicy('legal')}>{t('register.legalLink', 'Bases Legales')}</button>
        </label>
      </div>
      )}

      {/* RF-008 — el avatar se elige en un MODAL (como la maqueta), que se abre solo al aceptar los
          legales. Aquí queda sólo el resultado: la carita elegida, en la que se puede volver a pinchar
          para cambiarla — mientras no se haya enviado el alta.

          El avatar viaja en el cuerpo del alta, así que esta elección pasa con el formulario aún SIN
          enviar: si se eligiera después del envío ya no habría dónde meterlo sin una sesión, que es
          precisamente lo que no hay en esta pantalla anónima. */}
      {legalesAceptados && avatarId && (
        <div className="avatar-picker acc-field">
          {/* Sin `avatar-opt--on`: aquí no hay nada de qué distinguirlo —es el único que se muestra—,
              así que la marca de «seleccionado» no informa y su borde se pinta como un óvalo en un
              botón ancho. Reportado en QA el 2026-08-23. */}
          <button
            type="button"
            className="avatar-opt"
            onClick={() => setAbrirParaCambiar(true)}
            aria-label={t('avatar.change', 'Cambiar avatar')}
          >
            <UserAvatar avatarId={avatarId} name={fullName} size={72} />
          </button>
          <p className="avatar-sub">{t('avatar.change', 'Cambiar avatar')}</p>
        </div>
      )}

      {mostrarModalDeAvatar && (
        <AvatarPickerModal
          avatarIdActual={avatarId}
          onConfirmar={(id) => { setAvatarId(id); setAbrirParaCambiar(false); setError(null); }}
          onContinuarSinAvatar={() => { setRenuncioAlAvatar(true); setAbrirParaCambiar(false); }}
        />
      )}

      <button type="submit" className="acc-submit" disabled={register.isPending}>
        {register.isPending ? t('admin.saving', 'Guardando…') : t('register.submit', 'Registrarme')} <i className="fas fa-arrow-right" />
      </button>

      <AuthHelp />

      {/* Errores en modal (no debajo del formulario): un texto pequeño al fondo del formulario se
          confundía con el resto de leyendas de la pantalla y pasaba desapercibido. */}
      <AuthErrorModal
        message={avisoDelEnvio}
        onClose={() => {
          setError(null);
          // Sólo el 400 del avatar limpia la elección y recarga el catálogo. El resto de errores del
          // envío no tienen nada que ver con el avatar y no deben borrar lo elegido.
          if (avatarQuedoInvalido) { setAvatarId(null); void avatars.refetch(); }
          register.reset();
        }}
      />

      {policy && (
        <RegisterPolicyModal
          documentKey={policy}
          ouId={ouId}
          onClose={() => setPolicy(null)}
          onAccept={() => { if (policy === 'privacy') setAcceptPrivacy(true); else setAcceptLegal(true); }}
        />
      )}
    </form>
  );
}

/**
 * «Revisa tu correo»: lo que se ve al terminar el alta.
 *
 * Sustituye a la pantalla que decía «Ya puedes iniciar sesión», que desde este paquete sería falsa —
 * el login rechaza a quien no ha abierto el enlace, y con el mismo 401 que a una credencial mala.
 *
 * Trae el reenvío aquí y no sólo en el login porque este es el momento en que la persona descubre
 * que el correo no llegó: obligarla a irse al login para pedirlo otra vez es donde se pierde la gente.
 */
export function RevisaTuCorreo({ correo }: Readonly<{ correo: string }>) {
  const { t } = useTranslation('recognition');
  const resend = useResendVerification();
  const [restante, arrancarCooldown] = useCooldown(60);
  const [reenviado, setReenviado] = useState(false);

  return (
    <div className="acc-success" role="status">
      <div className="acc-success-icon">📬</div>
      <h2>{t('register.checkEmailTitle', 'Revisa tu correo')}</h2>
      <p>{t('register.checkEmailSub', 'Enviamos un enlace de verificación a {{email}}.', { email: correo })}</p>
      <p>{t('register.checkEmailHint', 'Abre el enlace para confirmar tu correo y poder iniciar sesión. Caduca en 72 horas.')}</p>

      <button
        type="button"
        className="acc-submit"
        disabled={resend.isPending || restante > 0}
        onClick={() => { resend.mutate(correo); setReenviado(true); arrancarCooldown(); }}
      >
        {restante > 0
          ? t('auth.retryIn', 'Puedes reintentar en {{seconds}} s', { seconds: restante })
          : t('register.resend', 'Reenviar el correo')}
      </button>

      {/* Mensaje ÚNICO: el servidor responde 202 pase lo que pase, así que esta pantalla no sabe —ni
          debe saber— si el correo salió de verdad. */}
      {reenviado && (
        <p role="status">{t('register.resendDone', 'Si tu cuenta está pendiente de verificar, te enviamos otro enlace.')}</p>
      )}

      {/* 🔴 La salida a login NO va aquí: la pinta `RegisterContent` debajo del formulario, y este
          bloque sustituye al formulario, no a aquélla. Ponerla también aquí la sacaba DOS VECES
          seguidas (visto en QA el 2026-08-18). Ver la prueba `no repite la salida a login`. */}
    </div>
  );
}

/**
 * Pantalla de verificación de correo (?token=) — el deep-link llama a verify-email y, al verificar,
 * invita a iniciar sesión. El avatar NO se elige aquí: esta página es anónima (sin sesión ni el id
 * real del usuario), así que el PATCH de avatar daría 401/404; se elige ya autenticado tras el login.
 */
function VerifyEmail({ token }: Readonly<{ token: string }>) {
  const { t } = useTranslation('recognition');
  const verify = useVerifyEmail(token);

  if (verify.isLoading) return <p role="status" style={{ opacity: 0.7 }}>{t('common.loading')}</p>;
  if (verify.isError || !verify.data) {
    return (
      <div className="acc-success" role="alert">
        <div className="acc-success-icon">⚠️</div>
        <h2>{t('register.verifyFailed')}</h2>
      </div>
    );
  }

  return (
    <div className="acc-success" role="status">
      <div className="acc-verified"><i className="fas fa-circle-check" /> {t('register.verified')}</div>
      <h2>{t('register.allSet')}</h2>
      <p>{t('register.allSetSub')}</p>
      <a className="acc-submit" href={withBase('/login')} style={{ display: 'inline-flex', textDecoration: 'none', marginTop: 16 }}>
        {t('login.submit', 'Iniciar sesión')} <i className="fas fa-arrow-right" />
      </a>
    </div>
  );
}

function RegisterContent() {
  const { t } = useTranslation('recognition');
  const [params] = useSearchParams();
  const token = useMemo(() => params.get('token'), [params]);

  return (
    <div className="acc-page">
      <div className="acc-topbar">
        <LanguageSelector />
      </div>
      <div className="acc-card">
        <AuthLogo />
        <h1 className="acc-title">{t('register.title')}</h1>
        <p className="acc-subtitle">{t('register.subtitle')}</p>
        {token ? <VerifyEmail token={token} /> : (
          <>
            {/* Propuesta de reestructura del login (pendiente de visto bueno del cliente) — quien
                YA tiene correo institucional no necesita este formulario; se le ofrece el acceso
                con cuenta Sigma antes de que empiece a llenar nada. Fuera del paso de verificación
                (`token`), donde este mensaje no tendría sentido. */}
            <EntraBlock
              title={t('register.entraTitle', '¿Ya tienes correo institucional?')}
              subtitle={t('register.entraSub', 'No necesitas registrarte — entra directo con tu cuenta Sigma.')}
              onEnter={() => { void msalInstance.loginRedirect(loginRequest); }}
            />
            <div className="acc-divider"><span>{t('login.or', 'o')}</span></div>

            <RegisterForm />
            {/* "Atrás" del mockup: vuelve al login (el registro es ruta aparte, no una vista alterna). */}
            <p className="acc-alt">
              <a href={withBase('/login')}>{t('register.backToLogin', '← Volver al inicio de sesión')}</a>
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export function FrontlineRegisterView() {
  return (
    <UserProvider>
      <ThemeProvider>
        <main className="acc-standalone">
          <RegisterContent />
        </main>
      </ThemeProvider>
    </UserProvider>
  );
}

export default FrontlineRegisterView;
